import { createClient } from "@supabase/supabase-js";

let _client = null;

/**
 * Crea (si no existe) y devuelve el cliente de Supabase.
 * - Lee credenciales desde import.meta.env (Vite): VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY
 * - Si ya existe window.__supabase (legacy), lo reutiliza.
 */
export function getSupabaseClient() {
  // 1) Si el legacy ya lo creó, respetarlo
  if (typeof window !== "undefined" && window.__supabase) {
    _client = window.__supabase;
    return _client;
  }

  // 2) Si ya lo creamos antes en este módulo, devolver
  if (_client) return _client;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anon) {
    // Importante: no “inventamos” cliente sin credenciales para evitar fallos silenciosos
    throw new Error(
      "Faltan variables de entorno: VITE_SUPABASE_URL y/o VITE_SUPABASE_ANON_KEY. " +
      "Añádelas en el archivo .env en la raíz del proyecto."
    );
  }

  _client = createClient(url, anon, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    global: {
      // Si luego necesitas headers custom, se añaden aquí sin tocar el resto del código
      headers: {},
    },
  });

  // 3) Compatibilidad legacy
  if (typeof window !== "undefined") {
    window.__supabase = _client;
  }

  return _client;
}

/**
 * Alias por si prefieres import { supabase } en otros módulos.
 * (Se inicializa perezosamente cuando se llama a getSupabaseClient)
 */
export function supabase() {
  return getSupabaseClient();
}

// =========================
// Auth helpers (mínimos, sin imponer UI)
// =========================

export async function getSession() {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.getSession();
  if (error) throw error;
  return data.session || null;
}

export async function getUser() {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.getUser();
  if (error) throw error;
  return data.user || null;
}

export async function signInWithPassword(email, password) {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data; // { user, session, ... }
}

export async function signOut() {
  const client = getSupabaseClient();
  const { error } = await client.auth.signOut();
  if (error) throw error;
  return true;
}

/**
 * Suscripción a cambios de auth.
 * Devuelve un unsubscribe() seguro.
 */
export function onAuthStateChange(callback) {
  const client = getSupabaseClient();
  const { data } = client.auth.onAuthStateChange((event, session) => {
    try {
      callback?.(event, session);
    } catch (e) {
      // No propagamos para no romper auth internamente
      console.error("[supabaseClient] Error en onAuthStateChange callback", e);
    }
  });

  // Supabase devuelve { subscription }
  return function unsubscribe() {
    try {
      data?.subscription?.unsubscribe?.();
    } catch (e) {
      console.warn("[supabaseClient] Error unsubscribe", e);
    }
  };
}

// =========================
// Utilidades comunes (sin acoplar al dominio)
// =========================

/**
 * Helper: normaliza errores de Supabase (por si luego quieres toast/telemetría).
 */
export function normalizeSupabaseError(err) {
  if (!err) return { message: "Error desconocido" };

  // Supabase suele traer { message, details, hint, code }
  const message =
    err?.message ||
    err?.error_description ||
    err?.details ||
    (typeof err === "string" ? err : "Error desconocido");

  return {
    message,
    code: err?.code,
    details: err?.details,
    hint: err?.hint,
    raw: err,
  };
}
